import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, TrendingUp, Target, CheckCircle2, XCircle, BarChart2, Zap, Trophy, Search, Crosshair, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)", border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)", green: "#22C55E", cyan: "#06B6D4", blue: "#3B82F6", red: "#EF4444", yellow: "#F59E0B", purple: "#A78BFA" };

const TYPE_LABELS = {
  "doppia": "Doppia Chance",
  "gol": "Mercato Gol",
  "1x2": "Esito 1X2",
  "multigol": "Multigol",
};

function AccuracyRing({ value, size = 120 }) {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  const color = value >= 60 ? C.green : value >= 45 ? C.yellow : C.red;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.highlight} strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-data text-2xl font-bold" style={{ color }}>{value}%</span>
        <span className="font-data text-xs" style={{ color: C.muted }}>precisione</span>
      </div>
    </div>
  );
}

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("30days");
  const [leagueSearch, setLeagueSearch] = useState("");
  const [gundamLeagueSearch, setGundamLeagueSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true); setError(null);
    axios.get(`${API}/stats?period=${period}`)
      .then((res) => setStats(res.data))
      .catch(() => setError("Errore nel caricamento statistiche."))
      .finally(() => setLoading(false));
  }, [period]);

  const filteredLeagueStats = useMemo(() => {
    if (!stats?.by_league) return [];
    if (!leagueSearch) return stats.by_league;
    const q = leagueSearch.toLowerCase();
    return stats.by_league.filter((l) => l.league.toLowerCase().includes(q) || l.country.toLowerCase().includes(q));
  }, [stats, leagueSearch]);

  const filteredGundamLeagueStats = useMemo(() => {
    if (!stats?.gundam?.by_league) return [];
    if (!gundamLeagueSearch) return stats.gundam.by_league;
    const q = gundamLeagueSearch.toLowerCase();
    return stats.gundam.by_league.filter((l) => l.league.toLowerCase().includes(q) || l.country.toLowerCase().includes(q));
  }, [stats, gundamLeagueSearch]);

  return (
    <div className="min-h-screen" style={{ background: C.bg }}>
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 mb-6 transition-opacity hover:opacity-70">
          <ArrowLeft size={16} style={{ color: C.muted }} />
          <span className="font-heading text-sm uppercase tracking-widest" style={{ color: C.muted }}>Torna alla Home</span>
        </button>

        <div className="flex items-center gap-3 mb-2">
          <BarChart2 size={22} style={{ color: C.yellow }} />
          <h1 className="font-heading font-bold text-2xl uppercase tracking-wider" style={{ color: C.text }}>LE MIE STATISTICHE</h1>
        </div>
        <p className="font-data text-sm" style={{ color: C.muted }}>Accuratezza dei pronostici Target Kitano nel tempo</p>

        {/* Period Selector */}
        <div className="flex gap-2 mt-5" data-testid="period-selector">
          {[
            { p: "today", l: "Oggi" },
            { p: "yesterday", l: "Ieri" },
            { p: "30days", l: "Ultimi 30 Giorni" },
          ].map(({ p, l }) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
              className="px-4 py-1.5 text-xs font-heading uppercase tracking-wider font-bold transition-all"
              style={{
                background: period === p ? C.yellow : C.surface,
                color: period === p ? "#09090B" : C.muted,
                border: `1px solid ${period === p ? C.yellow : C.border}`,
              }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 pb-12">

        {/* Informational banner */}
        <div className="mb-4 flex items-start gap-3 px-4 py-3" style={{ background: "rgba(6,182,212,0.05)", border: `1px solid rgba(6,182,212,0.15)` }}>
          <span style={{ color: C.cyan, fontSize: "0.9rem", flexShrink: 0 }}>ℹ</span>
          <p className="font-data text-xs leading-relaxed" style={{ color: C.muted }}>
            Le statistiche considerano <strong style={{ color: C.text }}>solo i campionati in whitelist</strong> (quelli attivi nell'app).
            I dati si popolano progressivamente: appaiono solo le partite già <strong style={{ color: C.text }}>analizzate e terminate</strong>.
            Nelle prime settimane il campione è ridotto — la precisione diventa significativa dopo ~50 partite valutate.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader2 size={20} className="animate-spin" style={{ color: C.yellow }} />
            <span className="font-heading text-sm" style={{ color: C.muted }}>Caricamento statistiche...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <p className="font-data text-sm" style={{ color: C.red }}>{error}</p>
          </div>
        )}

        {stats && !loading && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stats-overview">
              <div className="flex flex-col items-center justify-center py-6" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <AccuracyRing value={stats.accuracy} />
              </div>

              <div className="p-5 flex flex-col justify-center" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-2 mb-3">
                  <Target size={14} style={{ color: C.blue }} />
                  <span className="font-heading font-bold text-xs uppercase tracking-wider" style={{ color: C.muted }}>ANALIZZATE</span>
                </div>
                <p className="font-data text-3xl font-bold" style={{ color: C.text }}>{stats.total_analyzed}</p>
                <p className="font-data text-xs mt-1" style={{ color: C.muted }}>partite totali</p>
              </div>

              <div className="p-5 flex flex-col justify-center" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={14} style={{ color: C.green }} />
                  <span className="font-heading font-bold text-xs uppercase tracking-wider" style={{ color: C.muted }}>CORRETTI</span>
                </div>
                <p className="font-data text-3xl font-bold" style={{ color: C.green }}>{stats.total_correct}</p>
                <p className="font-data text-xs mt-1" style={{ color: C.muted }}>pronostici azzeccati</p>
              </div>

              <div className="p-5 flex flex-col justify-center" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-2 mb-3">
                  <XCircle size={14} style={{ color: C.red }} />
                  <span className="font-heading font-bold text-xs uppercase tracking-wider" style={{ color: C.muted }}>SBAGLIATI</span>
                </div>
                <p className="font-data text-3xl font-bold" style={{ color: C.red }}>{stats.total_wrong}</p>
                <p className="font-data text-xs mt-1" style={{ color: C.muted }}>pronostici falliti</p>
              </div>
            </div>

            {/* By Type Breakdown */}
            {stats.by_type.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="stats-by-type">
                <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <Zap size={14} style={{ color: C.yellow }} />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>PRECISIONE PER TIPO</span>
                </div>
                <div className="divide-y" style={{ borderColor: C.highlight }}>
                  {stats.by_type.map((t) => {
                    const accColor = t.accuracy >= 60 ? C.green : t.accuracy >= 45 ? C.yellow : C.red;
                    const pct = t.total > 0 ? (t.correct / t.total) * 100 : 0;
                    return (
                      <div key={t.type} className="flex items-center gap-4 px-4 py-3.5" data-testid={`type-${t.type}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading font-bold text-sm uppercase" style={{ color: C.text }}>{TYPE_LABELS[t.type] || t.type}</p>
                          <p className="font-data text-xs mt-0.5" style={{ color: C.muted }}>
                            {t.correct} corretti / {t.total} totali
                          </p>
                        </div>
                        <div className="w-32 hidden sm:block">
                          <div className="h-2 w-full" style={{ background: C.highlight }}>
                            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: accColor }} />
                          </div>
                        </div>
                        <div className="w-16 text-right">
                          <span className="font-data text-lg font-bold" style={{ color: accColor }}>{t.accuracy}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Daily Breakdown */}
            {stats.daily.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="stats-daily">
                <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <TrendingUp size={14} style={{ color: C.green }} />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>ANDAMENTO GIORNALIERO</span>
                </div>
                {/* Simple bar chart */}
                <div className="px-4 py-4">
                  <div className="flex items-end gap-2" style={{ height: 140 }}>
                    {stats.daily.map((d) => {
                      const maxTotal = Math.max(...stats.daily.map(x => x.total), 1);
                      const h = (d.total / maxTotal) * 120;
                      const accColor = d.accuracy >= 60 ? C.green : d.accuracy >= 45 ? C.yellow : C.red;
                      const dateStr = new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`day-${d.date}`}>
                          <span className="font-data text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accColor }}>
                            {d.accuracy}%
                          </span>
                          <div className="w-full relative" style={{ height: h }}>
                            <div className="absolute bottom-0 w-full transition-all"
                              style={{ height: `${(d.correct / d.total) * 100}%`, background: C.green, opacity: 0.7 }} />
                            <div className="absolute top-0 w-full transition-all"
                              style={{ height: `${(d.wrong / d.total) * 100}%`, background: C.red, opacity: 0.4 }} />
                          </div>
                          <span className="font-data" style={{ fontSize: "0.55rem", color: C.muted }}>{dateStr}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3" style={{ background: C.green, opacity: 0.7 }} />
                      <span className="font-data text-xs" style={{ color: C.muted }}>Corretti</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3" style={{ background: C.red, opacity: 0.4 }} />
                      <span className="font-data text-xs" style={{ color: C.muted }}>Sbagliati</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {stats.total_analyzed === 0 && (
              <div className="text-center py-16">
                <Target size={40} style={{ color: C.muted }} className="mx-auto mb-4 opacity-30" />
                <p className="font-heading text-lg" style={{ color: C.muted }}>Nessuna statistica disponibile</p>
                <p className="font-data text-sm mt-2" style={{ color: C.muted }}>Le statistiche saranno generate automaticamente quando le partite termineranno.</p>
              </div>
            )}

            {/* Per Campionato */}
            {stats.by_league && stats.by_league.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="stats-by-league">
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <div className="flex items-center gap-2">
                    <Trophy size={14} style={{ color: C.green }} />
                    <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>PER CAMPIONATO</span>
                    <span className="font-data text-xs" style={{ color: C.muted }}>{stats.by_league.length} campionati</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1" style={{ background: C.highlight, border: `1px solid ${C.border}` }}>
                    <Search size={12} style={{ color: C.muted }} />
                    <input data-testid="league-search" type="text" placeholder="Cerca campionato..."
                      value={leagueSearch} onChange={(e) => setLeagueSearch(e.target.value)}
                      className="bg-transparent text-xs outline-none font-data w-32"
                      style={{ color: C.text }} />
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {filteredLeagueStats.map((lg) => {
                    const accColor = lg.accuracy >= 70 ? C.green : lg.accuracy >= 50 ? C.yellow : C.red;
                    const pct = lg.total > 0 ? (lg.correct / lg.total) * 100 : 0;
                    return (
                      <div key={lg.league} className="flex items-center gap-3 px-4 py-3 group"
                        style={{ borderBottom: `1px solid ${C.highlight}` }}
                        data-testid={`league-stat-${lg.league.replace(/\s/g, '-')}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{lg.league}</p>
                          <p className="font-data text-xs mt-0.5" style={{ color: C.muted }}>
                            {lg.country} &middot; {lg.correct}/{lg.total} corretti
                          </p>
                        </div>
                        <div className="w-24 hidden sm:block">
                          <div className="h-1.5 w-full" style={{ background: C.highlight }}>
                            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: accColor }} />
                          </div>
                        </div>
                        <div className="w-14 text-right flex-shrink-0">
                          <span className="font-data text-sm font-bold" style={{ color: accColor }}>{lg.accuracy}%</span>
                        </div>
                      </div>
                    );
                  })}
                  {filteredLeagueStats.length === 0 && (
                    <div className="px-4 py-6 text-center">
                      <p className="font-data text-xs" style={{ color: C.muted }}>Nessun campionato trovato</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* GUNDAM SCORE Stats */}
            {stats.gundam && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${C.purple}` }} data-testid="gundam-stats-section">
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <Crosshair size={14} style={{ color: C.purple }} />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.purple }}>GUNDAM SCORE</span>
                  <span className="font-data text-xs" style={{ color: C.muted }}>— Precisione Risultati Esatti</span>
                </div>

                {stats.gundam.analyzed === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Crosshair size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.purple }} />
                    <p className="font-data text-sm" style={{ color: C.muted }}>Nessun Gundam Score ancora verificato.</p>
                    <p className="font-data text-xs mt-1" style={{ color: C.muted }}>I dati saranno disponibili quando le partite analizzate termineranno.</p>
                  </div>
                ) : (
                  <>
                    {/* Overview Cards */}
                    <div className="grid grid-cols-3 gap-3 p-4">
                      <div className="text-center py-4" style={{ background: C.highlight }}>
                        <span className="font-data text-2xl font-bold tabular-nums" style={{ color: C.purple }}>{stats.gundam.accuracy}%</span>
                        <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Precisione</p>
                        <p className="font-data text-[10px] mt-0.5" style={{ color: C.muted }}>almeno 1 score esatto</p>
                      </div>
                      <div className="text-center py-4" style={{ background: C.highlight }}>
                        <span className="font-data text-2xl font-bold tabular-nums" style={{ color: C.green }}>{stats.gundam.correct}</span>
                        <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Centrati</p>
                      </div>
                      <div className="text-center py-4" style={{ background: C.highlight }}>
                        <span className="font-data text-2xl font-bold tabular-nums" style={{ color: C.muted }}>{stats.gundam.analyzed}</span>
                        <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Analizzati</p>
                      </div>
                    </div>

                    {/* Gundam by league */}
                    {filteredGundamLeagueStats.length > 0 && (
                      <div style={{ borderTop: `1px solid ${C.border}` }}>
                        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                          <span className="font-heading text-xs uppercase tracking-wider" style={{ color: C.muted }}>Per Campionato</span>
                          <div className="flex items-center gap-1.5 px-2.5 py-1" style={{ background: C.highlight, border: `1px solid ${C.border}` }}>
                            <Search size={12} style={{ color: C.muted }} />
                            <input data-testid="gundam-league-search" type="text" placeholder="Cerca..."
                              value={gundamLeagueSearch} onChange={(e) => setGundamLeagueSearch(e.target.value)}
                              className="bg-transparent text-xs outline-none font-data w-28" style={{ color: C.text }} />
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {filteredGundamLeagueStats.map((lg) => {
                            const accColor = lg.accuracy >= 30 ? C.green : lg.accuracy >= 15 ? C.yellow : C.red;
                            const pct = lg.total > 0 ? (lg.correct / lg.total) * 100 : 0;
                            return (
                              <div key={lg.league} className="flex items-center gap-3 px-4 py-3"
                                style={{ borderBottom: `1px solid ${C.highlight}` }}
                                data-testid={`gundam-league-${lg.league.replace(/\s/g, '-')}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-heading font-bold text-xs uppercase truncate" style={{ color: C.text }}>{lg.league}</p>
                                  <p className="font-data text-xs mt-0.5" style={{ color: C.muted }}>
                                    {lg.country} &middot; {lg.correct}/{lg.total} esatti
                                  </p>
                                </div>
                                <div className="w-24 hidden sm:block">
                                  <div className="h-1.5 w-full" style={{ background: C.highlight }}>
                                    <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: accColor }} />
                                  </div>
                                </div>
                                <div className="w-14 text-right flex-shrink-0">
                                  <span className="font-data text-sm font-bold" style={{ color: accColor }}>{lg.accuracy}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* VALUE BET Stats */}
            {stats.value_bet && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid var(--bc-accent)` }} data-testid="value-bet-stats-section">
                <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <Star size={14} style={{ color: "var(--bc-accent)" }} />
                  <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: "var(--bc-accent)" }}>VALUE BET</span>
                  <span className="font-data text-xs" style={{ color: C.muted }}>— Precisione Vantaggio Statistico</span>
                </div>

                {stats.value_bet.analyzed === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Star size={32} className="mx-auto mb-3 opacity-20" style={{ color: "var(--bc-accent)" }} />
                    <p className="font-data text-sm" style={{ color: C.muted }}>Nessuna Value Bet ancora verificata.</p>
                    <p className="font-data text-xs mt-1" style={{ color: C.muted }}>
                      I dati appariranno man mano che le partite con vantaggio statistico termineranno.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3 p-4">
                    <div className="text-center py-5" style={{ background: C.highlight }}>
                      <span className="font-data text-3xl font-bold tabular-nums" style={{
                        color: stats.value_bet.accuracy >= 55 ? "#22C55E" : stats.value_bet.accuracy >= 40 ? C.yellow : C.red
                      }}>
                        {stats.value_bet.accuracy}%
                      </span>
                      <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Precisione</p>
                      <p className="font-data text-[10px] mt-0.5" style={{ color: C.muted }}>value bet corrette</p>
                    </div>
                    <div className="text-center py-5" style={{ background: C.highlight }}>
                      <span className="font-data text-3xl font-bold tabular-nums" style={{ color: C.green }}>{stats.value_bet.correct}</span>
                      <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Corrette</p>
                    </div>
                    <div className="text-center py-5" style={{ background: C.highlight }}>
                      <span className="font-data text-3xl font-bold tabular-nums" style={{ color: C.muted }}>{stats.value_bet.analyzed}</span>
                      <p className="font-data text-xs mt-1" style={{ color: C.muted }}>Analizzate</p>
                    </div>
                  </div>
                )}
                <div className="px-4 pb-3">
                  <p className="font-data text-xs" style={{ color: C.muted }}>
                    Traccia quante volte le previsioni con vantaggio statistico individuato dall'algoritmo si sono rivelate corrette, indipendentemente dal mercato.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
